import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { breadcrumb } from '../admin/layout/header/header.component';
import { DataService } from '../services/data.service';
import { MessageService, ConfirmationService, ConfirmEventType } from 'primeng/api';
import { PermissionHandlerService } from '../services/permission-handler.service';
import { TranslatorPipe } from '../services/translator.pipe';
import { ProductServicesFormComponent } from './product-services-form/product-services-form.component';

interface ServiceField {
  _id: string;
  label: string;
  type: string;
  data: string;
}

interface Service {
  _id?: string;
  name: string;
  type: string | null;
  fields: ServiceField[];
  category: string;
  tags: string[];
  pricing: {
    amount: number | string; // Allow both number and string (e.g., "not decided yet")
    currency: string;
    includesVAT: boolean;
  };
  status: string;
  active: boolean;
  tempInactive: boolean;
  signed: {
    status: boolean;
    user_id: string | null;
    user_name: string | null;
    date: string;
  };
  
  // Computed properties for display
  title?: string;
  purpose?: string;
  basePrice?: number | string; // Can be number or string (e.g., "not decided yet")
  basePriceCurrency?: string;
  basePriceIncludesVAT?: boolean;
  actionButtonText?: string;
  serviceDuration?: string;
  description?: {
    heading: string;
    text: string;
  };
  conditionsAndRequirements?: {
    heading: string;
    items: {
      text: string;
      isMet: boolean;
    }[];
  };
  targetAudience?: {
    heading: string;
    text: string;
  };
  otherInformation?: {
    heading: string;
    text: string;
  };
  isExpanded?: boolean;
}

@Component({
  selector: 'app-products-view',
  templateUrl: './products-services.component.html',
  styleUrl: './products-services.component.scss'
})
export class ProductsServicesComponent implements OnInit {
  breadcrumbs: breadcrumb[] = [
    {
      name: this.translate('Products and Services'),
      link: '/products-view',
      visible: true
    }
  ];

  servicesData: Service[] = [];
  allServicesData: Service[] = []; 
  spinner: boolean = false;
  totalServices: number = 0;
  first: number = 0;
  rows: number = 10;
  rejectReason: boolean = false;
  rejectData: any = {};
  
  // Dialog state
  showAddProductDialog: boolean = false;
  showOrderDialog: boolean = false;
  showConditionDetailsDialog: boolean = false;
  showProductDetailsDialog: boolean = false;
  selectedOU: any = null;
  isEditMode: boolean = false;
  editingService: any = null;
  selectedServiceForOrder: any = null;
  selectedConditionForDetails: any = null;
  selectedServiceForDetails: any = null;

  @ViewChild(ProductServicesFormComponent) productFormComponent!: ProductServicesFormComponent;

  // Filter properties
  searchText: string = '';
  statusOptions: any[] = [
    { name: 'Pending', value: 'PENDING', selected: false },
    { name: 'Approved', value: 'APPROVED', selected: false },
    { name: 'Active', value: 'ACTIVE', selected: false },
    { name: 'Published', value: 'PUBLISHED', selected: false },
    { name: 'Rejected', value: 'REJECTED', selected: false }
  ];

  constructor(
    private dataService: DataService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    public permissionService: PermissionHandlerService,
    private router: Router
  ) { }

  ngOnInit() {
    this.loadServices();
  }

  async loadServices() {
    this.spinner = true;
    
    try {
      // Calculate current page based on pagination
      const currentPage = Math.floor(this.first / this.rows) + 1;
      
      // Use the getAll endpoint with pagination
      const response = await this.dataService.getData(`product-service/${currentPage}/${this.rows}`);
      
      if (response.error) {
        console.error('Error loading services:', response.msg);
        this.allServicesData = [];
      } else {
        // Transform API data to Service interface format
        if (response.data && response.data.productServices && Array.isArray(response.data.productServices)) {
          this.allServicesData = response.data.productServices.map((apiData: any) => this.transformServiceData(apiData));
          
          // Update pagination info if available
          if (response.data.pagination) {
            this.totalServices = response.data.pagination.total || 0;
          }
        } else {
          this.allServicesData = [];
        }
      }
      
      // Apply filters
      this.applyFilters();
    } catch (error) {
      console.error('Error loading services:', error);
      this.allServicesData = [];
    } finally {
      this.spinner = false;
    }
  }

  private transformServiceData(apiData: any): Service {
    const service: Service = {
      _id: apiData._id,
      name: apiData.name,
      type: apiData.type,
      fields: apiData.fields,
      category: apiData.category,
      tags: apiData.tags,
      pricing: apiData.pricing,
      status: apiData.status,
      active: apiData.active,
      tempInactive: apiData.tempInactive,
      signed: apiData.signed,
      
      // Transform fields to display properties
      title: apiData.name,
      purpose: this.getFieldData(apiData.fields, 'Service Purpose'),
      basePrice: apiData.pricing.amount, // Can be number or string
      basePriceCurrency: apiData.pricing.currency,
      basePriceIncludesVAT: apiData.pricing.includesVAT,
      actionButtonText: this.translate('Get the Product'),
      serviceDuration: this.getFieldData(apiData.fields, 'Service Type'),
      
      description: {
        heading: this.getFieldLabel(apiData.fields, 'Service Description'),
        text: this.getFieldData(apiData.fields, 'Service Description')
      },
      
      conditionsAndRequirements: {
        heading: this.getFieldLabel(apiData.fields, 'Service Conditions and Requirements'),
        items: this.parseConditionsAndRequirements(this.getFieldData(apiData.fields, 'Service Conditions and Requirements'))
      },
      
      targetAudience: {
        heading: this.getFieldLabel(apiData.fields, 'Target Audience'),
        text: this.getFieldData(apiData.fields, 'Target Audience')
      },
      
      otherInformation: {
        heading: this.getFieldLabel(apiData.fields, 'Other Information'),
        text :  this.getFieldData(apiData.fields, 'Other Information')
        // perQueryPrice: this.extractPerQueryPrice(this.getFieldData(apiData.fields, 'Single Query Price')),
        // perQueryPriceCurrency: 'SAR',
        // perQueryPriceIncludesVAT: this.getFieldData(apiData.fields, 'Single Query Price').includes('including VAT')
      },
      
      isExpanded: false
    };
    
    return service;
  }

  private getFieldData(fields: ServiceField[], label: string): string {
    const field = fields.find(f => f.label === label);
    return field ? field.data : '';
  }

  private getFieldLabel(fields: ServiceField[], label: string): string {
    const field = fields.find(f => f.label === label);
    return field ? field.label : label;
  }

  private parseConditionsAndRequirements(data: string): { text: string; isMet: boolean }[] {
    if (!data) return [];
    
    // Split by bullet points or newlines
    const items = data.split(/[•\n]/).filter(item => item.trim());
    return items.map(item => ({
      text: item.trim(),
      isMet: true // Default to true, can be customized based on business logic
    }));
  }

  private extractPerQueryPrice(data: string): number {
    if (!data) return 0;
    
    // Extract number from text like "Price for querying a single financial statement is 195.5 SAR *including VAT"
    const match = data.match(/(\d+(?:\.\d+)?)/);
    return match ? parseFloat(match[1]) : 0;
  }

  applyFilters() {
    let filteredData = [...this.allServicesData];

    // Apply status filter
    const selectedStatuses = this.statusOptions.filter(status => status.selected);
    if (selectedStatuses.length > 0) {
      const statusValues = selectedStatuses.map(status => status.value);
      filteredData = filteredData.filter(service => statusValues.includes(service.status));
    }

    this.servicesData = filteredData;
  
  }

  applyFiltersWithoutSearch() {
    this.applyFilters();
  }

  onPageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
    // Reload data with new pagination parameters
    this.loadServices();
  }

  approveService(serviceId: string) {
    console.log('Approve service:', serviceId);
    const service = this.servicesData.find(s => s._id === serviceId);
    if (service) {
      service.status = 'APPROVED';
    }
  }

  activateService(service: Service) {
    console.log('Activate service:', service);
    service.status = 'ACTIVE';
  }

  editService(service: Service) {
    console.log('Edit service:', service);
  }

  publishService(serviceId: string) {
    console.log('Publish service:', serviceId);
    const service = this.servicesData.find(s => s._id === serviceId);
    if (service) {
      service.status = 'PUBLISHED';
    }
  }


  shouldShowAssignUsersButton(service: Service): boolean {
    return service.status === 'PUBLISHED' || service.status === 'ACTIVE';
  }

  changeActivation(serviceId: string, active: boolean) {
    console.log('Change activation for service:', serviceId, active);
    const service = this.servicesData.find(s => s._id === serviceId);
    if (service) {
      service.active = active;
    }
  }

  viewService(service: Service, readOnly: boolean = false) {
    console.log('View service:', service, 'Read only:', readOnly);
  }

  deleteService(serviceId: string) {
    if (!this.permissionService.hasPermission('PRODUCT_SERVICE', 'd')) {
        this.messageService.add({ severity: 'error', summary: this.translate('Access Denied'), detail: this.translate('You do not have permission to delete services.') });
      return;
    }

    console.log('Delete service:', serviceId);
    
    this.confirmationService.confirm({
      message: this.translate('Are you sure you want to delete this service?'),
      header: this.translate('Confirm Delete'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate('نعم'),
      rejectLabel: this.translate('لا'),
      accept: () => {
        this.performDelete(serviceId);
        return true;
      },
      reject: (type: any) => {
        switch (type) {
          case ConfirmEventType.REJECT:
            return false;
          case ConfirmEventType.CANCEL:
            return false;
          default:
            return false;
        }
      },
    });
  }

  async performDelete(serviceId: string) {
    try {
      this.spinner = true;
      
      const response = await this.dataService.deleteData(`product-service/${serviceId}`);
      
      if (response.error) {
        console.error('Error deleting service:', response.msg);
        this.messageService.add({ severity: 'error', summary: this.translate('Error'), detail: this.translate('Failed to delete service') });
      } else {
        console.log('Service deleted successfully');
        this.messageService.add({ severity: 'success', summary: this.translate('Success'), detail: this.translate('Service deleted successfully') });
        await this.loadServices();
      }
    } catch (error) {
      console.error('Error deleting service:', error);
      this.messageService.add({ severity: 'error', summary: this.translate('Error'), detail: this.translate('Failed to delete service') });
    } finally {
      this.spinner = false;
    }
  }

  // Dialog methods
  openAddProductDialog() {
    this.isEditMode = false;
    this.editingService = null;
    this.showAddProductDialog = true;
  }

  openEditProductDialog(service: Service) {
    this.isEditMode = true;
    this.editingService = this.transformServiceToFormData(service);
    console.log('Opening edit dialog with service:', service);
    console.log('Transformed data for form:', this.editingService);
    this.showAddProductDialog = true;
  }

  private transformServiceToFormData(service: Service): any {
    return {
      _id: service._id,
      name: service.name,
      category: service.category,
      type: service.type,
      pricing: {
        amount: service.pricing?.amount || 0,
        currency: service.pricing?.currency || 'SAR',
        includesVAT: service.pricing?.includesVAT || true
      },
      status: service.status,
      active: service.active,
      tempInactive: service.tempInactive || false,
      fields: service.fields || [],
      tags: service.tags || []
    };
  }

  closeAddProductDialog() {
    this.showAddProductDialog = false;
    this.selectedOU = null;
    this.isEditMode = false;
    this.editingService = null;
    // Redirect to products-view page when canceling
    this.router.navigate(['/products-view']);
  }

  onAddProductSubmit() {
    console.log('Add product form submitted');
    console.log('Selected OU:', this.selectedOU);
    this.closeAddProductDialog();
  }

  async onProductServiceSubmit(productService: any) {
    console.log('Product service form submitted:', productService);
    
    const requiredPermission = this.isEditMode ? 'u' : 'c';
    if (!this.permissionService.hasPermission('PRODUCT_SERVICE', requiredPermission)) {
        this.messageService.add({ 
          severity: 'error', 
          summary: this.translate('Access Denied'), 
          detail: this.translate(`You do not have permission to ${this.isEditMode ? 'update' : 'create'} services.`)
        });
      return;
    }
    
    try {
      this.spinner = true;
      
      if (this.isEditMode) {
        const response = await this.dataService.postData('product-service/', productService, 'PUT');
        if (response.error) {
          console.error('Error updating service:', response.msg);
          this.messageService.add({ severity: 'error', summary: this.translate('Error'), detail: this.translate('Failed to update service') });
          this.spinner = false;
          this.onFormError();
        } else {
          console.log('Service updated successfully');
          this.messageService.add({ severity: 'success', summary: this.translate('Success'), detail: this.translate('Service updated successfully') });
          this.closeAddProductDialog();
          this.onFormSuccess();
          this.loadServices();
        }
      } else {
        const response = await this.dataService.postData('product-service/', productService, 'POST');
        if (response.error) {
          console.error('Error creating service:', response.msg);
          this.messageService.add({ severity: 'error', summary: this.translate('Error'), detail: this.translate('Failed to create service') });
          this.spinner = false;
          this.onFormError();
        } else {
          console.log('Service created successfully');
          this.messageService.add({ severity: 'success', summary: this.translate('Success'), detail: this.translate('Service created successfully') });
          this.closeAddProductDialog();
          this.onFormSuccess();
          this.loadServices();
        }
      }
    } catch (error) {
      console.error('Error submitting service:', error);
      this.messageService.add({ severity: 'error', summary: this.translate('Error'), detail: this.translate('An error occurred while saving the service') });
      this.spinner = false;
      this.onFormError();
    }
  }

  onFormSuccess() {
    if (this.productFormComponent) {
      this.productFormComponent.onFormSuccess();
    }
  }

  onFormError() {
    if (this.productFormComponent) {
      this.productFormComponent.onFormError();
    }
  }

  getService(service: Service) {
    console.log('Get service clicked:', service);
    this.selectedServiceForOrder = service;
    this.showOrderDialog = true;
  }

  closeOrderDialog() {
    this.showOrderDialog = false;
    this.selectedServiceForOrder = null;
  }

  onOrderSubmit(event: any) {
    if (event.success) {
      this.messageService.add({
        severity: 'success',
        summary: this.translate('Success'),
        detail: this.translate('Order created successfully!')
      });
      this.closeOrderDialog();
    } else if (event.error) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate('Error'),
        detail: event.message || this.translate('Failed to create order. Please try again.')
      });
    }
  }

  onOrderCancel() {
    this.closeOrderDialog();
  }

  // Text truncation and modal methods
  isTextLong(text: string | undefined, maxLength: number = 100): boolean {
    return !!(text && text.length > maxLength);
  }

  getTruncatedText(text: string | undefined, maxLength: number = 100): string {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }

  // Individual condition details modal
  openConditionDetailsModal(condition: any) {
    this.selectedConditionForDetails = condition;
    this.showConditionDetailsDialog = true;
  }

  closeConditionDetailsDialog() {
    this.showConditionDetailsDialog = false;
    this.selectedConditionForDetails = null;
  }

  // Comprehensive product details modal
  openProductDetailsModal(service: Service) {
    this.selectedServiceForDetails = service;
    this.showProductDetailsDialog = true;
  }

  closeProductDetailsDialog() {
    this.showProductDetailsDialog = false;
    this.selectedServiceForDetails = null;
  }


  // Filter methods
  async onSearchChange(searchText: string) {
    this.searchText = searchText;
    console.log('Search text changed:', searchText);
    
    if (searchText && searchText.trim()) {
      await this.searchServices(searchText.trim());
    } else {
      await this.loadServices();
    }
  }

  async searchServices(searchTerm: string) {
    this.spinner = true;
    
    try {
      const currentPage = Math.floor(this.first / this.rows) + 1;
      
      // Use the search endpoint
      const response = await this.dataService.getData(`product-service/search/${encodeURIComponent(searchTerm)}/${currentPage}/${this.rows}`);
      
      if (response.error) {
        console.error('Error searching services:', response.msg);
        this.allServicesData = [];
      } else {
        // Transform API data to Service interface format
        if (response.data && response.data.productServices && Array.isArray(response.data.productServices)) {
          this.allServicesData = response.data.productServices.map((apiData: any) => this.transformServiceData(apiData));
          
          // Update pagination info if available
          if (response.data.pagination) {
            this.totalServices = response.data.pagination.total || 0;
          }
        } else {
          this.allServicesData = [];
        }
      }
      
      this.applyFiltersWithoutSearch();
    } catch (error) {
      console.error('Error searching services:', error);
      this.allServicesData = [];
    } finally {
      this.spinner = false;
    }
  }

  onStatusFilterChange() {
    this.applyFilters();
  }

  refreshFilters() {
    this.searchText = '';
    this.statusOptions.forEach(status => status.selected = false);
    this.applyFilters();
  }

  translate(key: string): string {
    let translator: TranslatorPipe = new TranslatorPipe();
    return translator.transform(key);
  }

  navigateToProductTypes() {
    this.router.navigate(['/products-view/product-type']);
  }

  toggleServiceExpansion(service: Service) {
    service.isExpanded = !service.isExpanded;
  }

  /**
   * Check if a price value is numeric
   * @param value The price value (number or string)
   * @returns true if the value is a valid number
   */
  isNumericPrice(value: number | string | undefined): boolean {
    if (value === undefined || value === null) return false;
    if (typeof value === 'number') return true;
    if (typeof value === 'string') {
      // Check if string is a valid number (including decimals)
      const numValue = parseFloat(value);
      return !isNaN(numValue) && isFinite(numValue) && value.trim() !== '';
    }
    return false;
  }

  /**
   * Format price for display - shows number with currency or text as-is
   * @param amount The price amount (number or string)
   * @param currency The currency symbol
   * @returns Formatted price string
   */
  formatPriceDisplay(amount: number | string | undefined, currency: string = 'SAR'): string {
    if (amount === undefined || amount === null) return '';
    if (this.isNumericPrice(amount)) {
      const numAmount = typeof amount === 'number' ? amount : parseFloat(amount);
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numAmount);
    }
    // Return text as-is if not numeric
    return String(amount);
  }
}
